import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import "@bizop/ui-kit/globals.css";

import { App } from "./App";

/**
 * Bootstrap the admin app.
 *
 * If `VITE_USE_AUTH=true` is set AND we have no session token in
 * localStorage, redirect to /login BEFORE React hydrates. This avoids
 * a flash of the authenticated shell. While the flag is off (default),
 * the app boots straight into the dev-stub experience.
 *
 * Real session-token wiring lands when `auth.config.ts` is filled in.
 */
const useAuth =
  (import.meta.env.VITE_USE_AUTH ?? "").toString().toLowerCase() === "true";
const SESSION_KEY = "bizop-session";

/**
 * Validate that the session stored in localStorage is a well-formed,
 * non-expired object — not just any truthy string. This prevents privilege
 * escalation via `localStorage.setItem("bizop-session", "1")`.
 */
function isSessionValid(): boolean {
  try {
    const raw = window.localStorage.getItem(SESSION_KEY);
    if (!raw) return false;
    const session = JSON.parse(raw);
    if (!session.token || typeof session.token !== "string") return false;
    if (!session.expiresAt || typeof session.expiresAt !== "number") return false;
    if (Date.now() > session.expiresAt) {
      // Expired — clean up
      window.localStorage.removeItem(SESSION_KEY);
      return false;
    }
    return true;
  } catch {
    // Malformed JSON — wipe it
    window.localStorage.removeItem(SESSION_KEY);
    return false;
  }
}

if (
  useAuth &&
  typeof window !== "undefined" &&
  !isSessionValid() &&
  window.location.pathname !== "/login"
) {
  window.location.replace("/login");
}

/**
 * When InstantDB is configured, purge any leftover "demo:" localStorage entries
 * once at boot. Old localStorage data could otherwise leak back into pages that
 * still read from demoStore as a fallback. With InstantDB as the source of
 * truth, localStorage cache is just dead weight.
 *
 * We do this BEFORE React renders so no component sees stale data.
 * Onboarding/PWA flags are kept (they're not in the "demo:" namespace).
 */
const hasInstantAppId = (() => {
  const raw = import.meta.env.VITE_INSTANT_APP_ID;
  if (!raw) return false;
  const trimmed = String(raw).trim();
  if (!trimmed) return false;
  if (trimmed.includes("your-app-id") || trimmed.includes("xxxxx")) return false;
  return true;
})();

if (hasInstantAppId && typeof window !== "undefined") {
  const PURGED_KEY = "bizop-demo-purged-v1";
  if (!window.localStorage.getItem(PURGED_KEY)) {
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < window.localStorage.length; i++) {
        const k = window.localStorage.key(i);
        if (k?.startsWith("demo:")) keysToRemove.push(k);
      }
      for (const k of keysToRemove) {
        window.localStorage.removeItem(k);
      }
      window.localStorage.setItem(PURGED_KEY, String(Date.now()));
      if (import.meta.env.DEV && keysToRemove.length > 0) {
        console.info(
          `[bizop] Purged ${keysToRemove.length} leftover demo:* localStorage entries on first InstantDB boot.`,
        );
      }
    } catch {
      /* private browsing / quota — ignore */
    }
  }
}

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("Root element #root not found in index.html");

createRoot(rootEl).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
